'use client'

import React, { use } from 'react'
import { toast } from 'sonner'
import { useCheckout } from '@/lib/hooks'
import { setPaymentMethodType } from '@/lib/redux'

import { CardValidationInput, Thumbnail } from '@/components/ui'
import { CardLastFour } from '@/components/ui/icons'
import { RefContext, Refs } from '@/components/providers/ref-provider'

interface PaymentCardVerificationProps
  extends React.ComponentPropsWithoutRef<'form'> {
  onClose: () => void
}

export const PaymentCardVerification = ({
  onClose,
}: PaymentCardVerificationProps) => {
  const { amountInputRef } = use(RefContext) as Refs
  const { payment_method, setCurrentStep, setLastDigits, setFirstDigits } =
    useCheckout()
  const isFirstFourRequired = payment_method?.bin_required === 1
  const isLastFourRequired = payment_method?.last_digits_required === 1

  const handleSubmit = (data: FormData) => {
    const firstFour = data.get('first-four-digits') as string
    const lastFour = data.get('last-four-digits') as string

    if (!firstFour && isFirstFourRequired) {
      return toast.error('First four digits are required')
    } else if (
      isFirstFourRequired &&
      (firstFour.length !== 4 || isNaN(Number(firstFour)) === true)
    ) {
      return toast.error('Invalid first four digits')
    }

    if ((!lastFour || lastFour === '') && isLastFourRequired) {
      return toast.error('Last four digits are required')
    } else if (
      isLastFourRequired &&
      (lastFour.length !== 4 || isNaN(Number(lastFour)) === true)
    ) {
      return toast.error('Invalid last four digits')
    }

    setLastDigits(lastFour as string)
    setFirstDigits(firstFour as string)
    setPaymentMethodType('card')
    setCurrentStep('COLLECT_PAYMENT')
    amountInputRef?.current?.focus()
    onClose?.()
  }

  return (
    <form className="flex w-full gap-5" action={handleSubmit}>
      <div className="flex flex-grow gap-6 rounded-lg border border-gray-100 p-6 dark:border-gray-700">
        <span className="hidden h-[128px] w-[192px] shrink-0 lg:flex">
          <CardLastFour />
        </span>

        <div className="flex w-full flex-col gap-2">
          <div className="flex w-full flex-col gap-2">
            <div className="flex items-center gap-1.5">
              <Thumbnail
                size="xs"
                src={payment_method?.logo_url}
                className="rounded-md border border-gray-100 !bg-white p-0.5"
              />
              <span className="text-base font-medium">
                {payment_method?.name}
              </span>
            </div>

            <CardValidationInput
              isFirstFourRequired={isFirstFourRequired}
              isLastFourRequired={isLastFourRequired}
            />
          </div>

          <span className="text-sm text-gray-700">
            Enter first and last 4 digits of the card
          </span>
        </div>
      </div>
    </form>
  )
}
